/**
 * Field Sales landing — the rep's workspace.
 *
 *   ┌──────────────────────────────────────────────────┐
 *   │  [TODAY'S PLAN]  [MY TERRITORIES]  [TEAM CHAT ②]  │  ← tabs
 *   ├──────────────────────────────────────────────────┤
 *   │   < tab contents >                               │
 *   └──────────────────────────────────────────────────┘
 *
 * The first two tabs render the same FollowUpTracker component —
 * identical table shape, identical filters, identical rep actions.
 * The difference is the data source:
 *
 *   * Today's Plan tab → useTodaysPlanData
 *       Returns a single synthesized bucket containing the
 *       top-N curated accounts ranked across every real bucket.
 *
 *   * My Territories tab (tab value "tracker") → useFieldFollowUp
 *       Returns the standard 5 buckets (Nurture / Check In /
 *       Re-Engage / Win Back / At Risk), filtered to the rep's
 *       territory states — their full account list, vs. Today's
 *       Plan's curated short list. Reps with access to the
 *       Depletions section see this same shape there (company-wide).
 *
 * The fourth tab is a different animal: Team Chat is person-to-person
 * correspondence (see NotesInbox), not account data. It carries an
 * unread badge so a rep sees incoming notes without opening the tab.
 */

import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import EditNoteIcon from '@mui/icons-material/EditNote';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  Container,
  LinearProgress,
  Slide,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import type { DataWindow, FollowUpAccount, FollowUpTrackerResponse } from '../../api/depletions';
import {
  TODAY_PLAN_BUCKET_LABEL,
  fetchMyRepProfile,
  pinAccount,
  recordSkip,
  unpinAccount,
  useFieldFollowUp,
  useFieldStates,
  useTodaysPlanData,
} from '../../api/field';
import { useUnreadNoteCount } from '../../api/notes';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../auth/useAuth';
import { missingCoreWhiskey } from '../../lib/coreWhiskey';
import { FollowUpTracker, TodaysPlanRankingInfo } from '../../components/FollowUpTracker';
import type { SectionExplainerRow } from '../../components/SectionExplainer';

import { isRepSurfacePath, useActAsRep } from '../../api/actAs';
import { MomentumPanel } from '../../components/MomentumPanel';
import { SiteLayout } from '../../components/SiteLayout';
import { TerritoryStates } from '../../components/TerritoryStates';
import { coverageRangeLabel, parseBackendDate } from '../../util/dates';
import {
  accountColumns,
  excelSheet,
  isoDate,
  num,
  premisesLabel,
  type ExcelWorkbookSpec,
  type ExportAbout,
  type ExportFilter,
} from '../../util/excel-export';
import { AddAccountDialog } from './AddAccountDialog';
import { SectionExportButton, SectionExportProvider } from '../../components/SectionExport';
import { REGIONAL_SALES_TABS } from '../../data/regionalSalesGuide';
import { AddedAccountsTab } from './AddedAccountsTab';
import { MyLogsTab } from './MyLogsTab';
import { NoShipmentsTab } from './NoShipmentsTab';
import { FlagAccountDialog } from './FlagAccountDialog';
import { NotesInbox } from '../../components/NotesInbox';
import { getTodayReason, nearHome, TODAY_PLAN_LIMIT, todayScore } from './todayScore';
import { VisitNoteDialog } from './VisitNoteDialog';

const INK = '#0f0a07';
const GOLD = '#bb8c3f';
const IVORY = '#fdf9f0';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

// Brand-styled tooltip — cream background, gold border, ink text.
// MUI's default tooltip is dark grey which clashes with the HY
// editorial palette.  Reuse this slotProps object on any <Tooltip>
// that lives on a brand-styled surface (Today's Plan rows, the
// territory panel, etc.).  Inner Typography elements override
// font-size / font-weight when passing structured content via
// FormattedTooltipBody.
const BRAND_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      bgcolor: IVORY,
      color: INK,
      border: '1px solid rgba(187, 140, 63, 0.42)',
      fontFamily: FONT_INTER,
      fontSize: 12,
      fontWeight: 500,
      maxWidth: 320,
      px: 1.5,
      py: 1.1,
      lineHeight: 1.5,
      boxShadow: '0 6px 18px rgba(15, 10, 7, 0.18)',
    },
  },
  arrow: {
    sx: {
      color: IVORY,
      '&::before': {
        border: '1px solid rgba(187, 140, 63, 0.42)',
        backgroundColor: IVORY,
      },
    },
  },
};

// Editorial tooltip content — gold eyebrow caps + ink headline +
// muted body line.  Pairs with BRAND_TOOLTIP_SLOT_PROPS for any
// tooltip on Today's Plan rows so the hover reads as a tiny labeled
// card rather than a long sentence.
function FormattedTooltipBody({
  eyebrow,
  title,
  body,
}: {
  eyebrow: string;
  title: string;
  body?: string;
}) {
  return (
    <Box sx={{ py: 0.15, minWidth: 180 }}>
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: GOLD,
          mb: 0.4,
          lineHeight: 1,
        }}
      >
        {eyebrow}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 12.5,
          fontWeight: 700,
          color: INK,
          lineHeight: 1.35,
        }}
      >
        {title}
      </Typography>
      {body && (
        <Typography
          sx={{
            mt: 0.4,
            fontFamily: FONT_INTER,
            fontSize: 11.5,
            fontWeight: 500,
            color: 'rgba(15, 10, 7, 0.6)',
            lineHeight: 1.45,
          }}
        >
          {body}
        </Typography>
      )}
    </Box>
  );
}

type Mode = 'today' | 'tracker' | 'added' | 'noship' | 'logs' | 'notes';

// Tab → its User Guide topic, whose copy describes the tab on the
// Export Info sheet of a downloaded file.
const MODE_GUIDE_ID: Partial<Record<Mode, string>> = {
  today: 'todays-plan',
  tracker: 'my-territories',
  added: 'manually-added',
  noship: 'no-shipments',
  logs: 'my-logs',
};

function exportAboutFor(mode: Mode): ExportAbout | undefined {
  const topic = REGIONAL_SALES_TABS.find((t) => t.id === MODE_GUIDE_ID[mode]);
  return topic ? { description: topic.guide.what } : undefined;
}

const OUTCOME_LABEL: Record<string, string> = {
  ordered: 'Ordered',
  follow_up_needed: 'Follow-up',
  no_response: 'No response',
  declined: 'Declined',
  info_only: 'Info only',
};

const OUTCOME_TONE: Record<string, string> = {
  ordered: '#22865a',
  follow_up_needed: '#a86b1e',
  no_response: '#7a7a78',
  declined: '#a13a3a',
  info_only: '#326eb8',
};

// Returns true if the rep already logged a visit / call for this
// account TODAY (rep's local timezone).  Drives the "Logged"
// checkmark + fade on Today's Plan rows — keeps the rep visually
// aware of what's done without hard-blocking the unlogged rows.
function isLoggedToday(lastVisitDateIso: string | null | undefined): boolean {
  if (!lastVisitDateIso) return false;
  // Date-only string: parse as a local day (new Date() would read it as
  // UTC midnight — the previous evening anywhere in the US).
  const visit = parseBackendDate(lastVisitDateIso);
  if (isNaN(visit.getTime())) return false;
  const now = new Date();
  return (
    visit.getFullYear() === now.getFullYear() &&
    visit.getMonth() === now.getMonth() &&
    visit.getDate() === now.getDate()
  );
}

function daysAgoLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = parseBackendDate(iso);
  if (isNaN(d.getTime())) return null;
  const days = Math.round((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  if (days < 60) return '1 mo ago';
  return `${Math.floor(days / 30)} mo ago`;
}

function formatDataWindow(window: DataWindow | null): string | null {
  if (!window) return null;
  // coverageRangeLabel uses the timezone-safe formatter: the API sends
  // date-only strings (first-of-month), and `new Date(iso)` would parse
  // them as UTC and roll back a whole month in negative-UTC-offset
  // zones — the bug that rendered "Jan 2024 – Jun 2026" as "Dec 2023 –
  // May 2026". It also appends the day-level cutoff, so a rep can see
  // the newest month is still filling in.
  return coverageRangeLabel(window.earliest, window.latest, window.data_through);
}

// =====================================================================
// "How to read this" — reader's guide tailored to Today's Plan.
// The FollowUpTracker's default rows describe the My Territories /
// Depletions chrome (pipeline pills, momentum chips, action flags).
// Today's Plan uses different chrome (curated picks, "Why now" cell,
// LOG VISIT CTA, Logged-today fade), so we pass an alternate set of
// rows through the `explainerRows` prop.  Defined module-level so
// React doesn't rebuild the sample DOM nodes every render.
// =====================================================================
const SAMPLE_CHIP_BASE = {
  display: 'inline-flex',
  alignItems: 'center',
  fontFamily: FONT_INTER,
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: '0.04em',
  height: 20,
  borderRadius: '12px',
  px: 1,
  whiteSpace: 'nowrap' as const,
};

const TODAY_PLAN_EXPLAINER_ROWS: SectionExplainerRow[] = [
  {
    sample: (
      <Box
        component="span"
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: GOLD,
          bgcolor: 'rgba(187, 140, 63, 0.12)',
          border: '1px solid rgba(187, 140, 63, 0.42)',
          px: 0.85,
          py: 0.2,
          borderRadius: 0.75,
        }}
      >
        Top picks
      </Box>
    ),
    term: 'How the day’s picks are chosen',
    desc: (
      <>
        Up to <strong>20 accounts</strong> ranked across every bucket. Pinned accounts always land
        first; the rest are weighted by <strong>proximity to your home metro</strong>,{' '}
        <strong>declining momentum</strong>, fresh activity, and lifetime 6-pk volume. Accounts in
        cooldown from a recent visit are suppressed.
      </>
    ),
  },
  {
    sample: (
      <Box
        component="span"
        sx={{
          ...SAMPLE_CHIP_BASE,
          color: '#326eb8',
          bgcolor: 'rgba(50, 110, 184, 0.10)',
          border: '1px solid rgba(50, 110, 184, 0.33)',
        }}
      >
        Sweet spot
      </Box>
    ),
    term: '"Why now" chip',
    desc: (
      <>
        The single dominant reason this account made today’s list — e.g.{' '}
        <strong>Declining 50%</strong>, <strong>Sweet spot for re-order</strong>,{' '}
        <strong>New activity</strong>, <strong>Pinned by you</strong>. Tone of the chip matches the
        urgency.
      </>
    ),
  },
  {
    sample: (
      <Box
        component="span"
        sx={{
          ...SAMPLE_CHIP_BASE,
          fontSize: 10,
          color: GOLD,
          bgcolor: 'rgba(187, 140, 63, 0.08)',
          border: '1px solid rgba(187, 140, 63, 0.30)',
        }}
      >
        Near you
      </Box>
    ),
    term: 'Proximity chip',
    desc: (
      <>
        <strong>In your ZIP</strong> = same 5-digit ZIP as your home address.{' '}
        <strong>Near you</strong> = same metro (ZIP-3 sectional center). Helps you route a day’s
        visits without bouncing across the territory.
      </>
    ),
  },
  {
    sample: (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          fontFamily: FONT_INTER,
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: GOLD,
          background: 'linear-gradient(180deg, #ffffff 0%, #fdf9f0 100%)',
          border: '1px solid rgba(187, 140, 63, 0.55)',
          boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 1px 2px rgba(15, 10, 7, 0.05)',
          px: 1,
          py: 0.3,
          borderRadius: 0.75,
        }}
      >
        Log Visit
      </Box>
    ),
    term: 'Log Visit (mandatory)',
    desc: (
      <>
        Every account you work needs a visit note. The dialog captures the channel (in-person /
        call), the outcome (Ordered, Follow-up needed, No response, Declined, Info only), and a
        free-form note. The outcome you pick drives the <strong>cooldown</strong> — how soon the
        account can resurface on Today’s Plan.
      </>
    ),
  },
  {
    sample: (
      <Box
        component="span"
        sx={{
          ...SAMPLE_CHIP_BASE,
          color: '#22865a',
          bgcolor: 'rgba(34, 134, 90, 0.12)',
          border: '1px solid rgba(34, 134, 90, 0.45)',
        }}
      >
        ✓ Logged today
      </Box>
    ),
    term: 'Logged-today state',
    desc: (
      <>
        Once you submit a visit note, the row{' '}
        <Box component="span" sx={{ opacity: 0.55 }}>
          fades
        </Box>{' '}
        and the secondary icons swap for a green "Logged today" badge plus the outcome you picked.
        Logged rows sort to the bottom so unfinished work stays on top.
      </>
    ),
  },
  {
    sample: (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          fontFamily: FONT_INTER,
          fontSize: 10,
          fontWeight: 700,
          color: 'rgba(15, 10, 7, 0.55)',
        }}
      >
        📌 ✕ 🚩
      </Box>
    ),
    term: 'Pin · Skip · Flag',
    desc: (
      <>
        <strong>Pin</strong> always keeps an account at the top of tomorrow’s picks.{' '}
        <strong>Skip</strong> hides the row from today’s view only (no DB write).{' '}
        <strong>Flag</strong> escalates an account-level issue to admin review (closed store,
        ownership change, data correction) — separate from a visit outcome.
      </>
    ),
  },
];

export function FieldPage() {
  const [mode, setMode] = useState<Mode>('today');
  // Lives here rather than in TodaysPlanTab because the button now
  // sits in the page header, above the tabs.
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  // The Added tab logs visits through the same dialog Today's Plan
  // uses; it lives here because the tab is a sibling, not a child.
  const [logVisitTarget, setLogVisitTarget] = useState<{
    accountId: number;
    accountName: string;
  } | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Unread badge on the Team Chat tab.  Polled (see useUnreadNoteCount)
  // so a note arriving while the rep sits on Today's Plan still
  // surfaces without a page refresh.
  const unreadNotes = useUnreadNoteCount().data?.unread_count ?? 0;

  const profileQuery = useQuery({
    queryKey: ['field', 'me'],
    queryFn: fetchMyRepProfile,
    staleTime: 60_000,
  });
  const rep = profileQuery.data;
  const territoryStates = useMemo(() => rep?.territory_states ?? [], [rep?.territory_states]);

  // Every state with accounts, so the filter can offer somewhere the rep
  // does not cover. Their own states are listed first; the rest sit under
  // "Other states" — visible, but never mistakable for their patch.
  const statesQuery = useFieldStates();
  const allStateOptions = useMemo(() => {
    const all = statesQuery.data?.states ?? [];
    // Fall back to the territory alone until the list arrives, so the
    // filter is never momentarily empty.
    return all.length > 0 ? all : territoryStates.slice().sort();
  }, [statesQuery.data, territoryStates]);

  // My Territories drives its own fetch: picking a state outside the
  // territory has to reach the server, since the default response only
  // covers the rep's own states.
  const [territoryStateFilter, setTerritoryStateFilter] = useState<string[]>([]);
  const useTerritoryData = () => useFieldFollowUp(territoryStateFilter);

  const { user } = useAuth();
  const isRealRep = !!user?.roles.some((r) => r.name === 'field_rep');
  const isAdminViewer = !isRealRep && !!user?.roles.some((r) => r.name === 'admin');

  // Only while an admin is viewing AS someone. The API is
  // impersonation-aware, so the panel here shows that rep's window —
  // which is the point of the view-as page, and the one place an admin
  // can see an individual's grid in the context of their actual work.
  //
  // A rep on their own page doesn't get it: they already passed it on
  // the home page they landed on, and repeating it here just pushes
  // Today's Plan down the screen.
  // Path-gated, like the API client and the banner. FieldPage renders
  // at BOTH /regional-sales (your own workspace) and the view-as route,
  // so the stored value alone can't tell them apart — reading it raw
  // put the panel on a rep's own page, duplicating the one they had
  // just scrolled past on the home page.
  const { pathname } = useLocation();
  const storedActAs = useActAsRep();
  const actingAsRep = isRepSurfacePath(pathname) && storedActAs !== null;

  return (
    <SiteLayout subtitle="Regional Sales" showDesktopHint>
      <Container maxWidth="xl" sx={{ pt: { xs: 3, md: 5 }, pb: { xs: 4, md: 6 } }}>
        {/* Heading */}
        {/* Editorial hero — magazine-style header:
              1. Tiny gold eyebrow ("Regional Sales") that anchors the
                 section in the wider product nav.
              2. Large ink H1 ("Your Sales Floor") set tight.
              3. One-line italic dek that frames the page's purpose.
            The previous two-column "Today's Plan | Browse Accounts"
            brief was redundant with the tabs immediately below it and
            crowded the visual rhythm — removed. The tab descriptions
            still live in the in-table chrome (progress strip + Top
            picks banner) where reps actually look. */}
        <Box sx={{ mb: { xs: 2.5, md: 3 } }}>
          {/* Heading row: title block on the left, the page's primary
              action opposite it. "Add an account" previously sat loose
              between the progress strip and the picks table, where it
              read as an afterthought — it belongs up here with the
              other page-level chrome, where it's visible before the rep
              starts scanning rows. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '0.28em',
                  textTransform: 'uppercase',
                  color: GOLD,
                  mb: 0.75,
                }}
              >
                Regional Sales
              </Typography>
              <Typography
                component="h1"
                sx={{
                  fontFamily: FONT_INTER,
                  fontWeight: 700,
                  fontSize: { xs: 26, md: 34 },
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                  color: INK,
                }}
              >
                Your Sales Floor
              </Typography>
              <Typography
                sx={{
                  mt: 0.75,
                  maxWidth: 640,
                  fontFamily: FONT_INTER,
                  fontStyle: 'italic',
                  fontSize: { xs: 13.5, md: 14.5 },
                  color: 'rgba(15, 10, 7, 0.62)',
                  lineHeight: 1.55,
                }}
              >
                A curated short list for today, with your full territory one tab away.
              </Typography>
            </Box>

            {/* Hidden on the Notes tab, where adding an account has
                nothing to do with what's on screen. */}
            {mode !== 'notes' && (
              <Button
                variant="contained"
                startIcon={<AddBusinessOutlinedIcon sx={{ fontSize: 18 }} />}
                onClick={() => setAddAccountOpen(true)}
                sx={{
                  flexShrink: 0,
                  fontFamily: FONT_INTER,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: 13.5,
                  px: 2,
                  py: 1,
                  borderRadius: 1.5,
                  bgcolor: GOLD,
                  boxShadow: '0 2px 8px rgba(139, 101, 38, 0.24)',
                  transition: 'transform 160ms ease, box-shadow 160ms ease',
                  '&:hover': {
                    bgcolor: '#a67c34',
                    boxShadow: '0 4px 14px rgba(139, 101, 38, 0.30)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': { transform: 'translateY(0)' },
                }}
              >
                Add an Account
              </Button>
            )}
          </Box>

          {actingAsRep && (
            <Box sx={{ mt: 2.5 }}>
              <MomentumPanel />
            </Box>
          )}

          {territoryStates.length > 0 && (
            <Box sx={{ mt: 2.5 }}>
              <TerritoryPanel territoryStates={territoryStates} />
            </Box>
          )}
        </Box>

        {/* Admin-viewer banner */}
        {isAdminViewer && (
          <Alert severity="info" sx={{ mb: 3, fontFamily: FONT_INTER, borderRadius: 1.25 }}>
            This page is where sales representatives work their accounts. To oversee everyone, open{' '}
            <Box component={Link} to="/admin/regional-sales" sx={{ color: GOLD, fontWeight: 600 }}>
              Admin → Regional Sales
            </Box>
            . To use it as a representative yourself, add the Regional Sales Representative role to
            your account.
          </Alert>
        )}

        {/* Profile incomplete hint */}
        {isRealRep && rep && !rep.home_zip && (
          <Alert severity="info" sx={{ mb: 3, fontFamily: FONT_INTER, borderRadius: 1.25 }}>
            Add your home address from{' '}
            <Box component={Link} to="/profile" sx={{ color: GOLD, fontWeight: 600 }}>
              Account Settings
            </Box>{' '}
            so the route view can show distance from home.
          </Alert>
        )}

        {/* No territory hint */}
        {isRealRep && territoryStates.length === 0 && !profileQuery.isLoading && (
          <Alert severity="warning" sx={{ mb: 3, fontFamily: FONT_INTER }}>
            You don’t have any territory states assigned yet. Ask an admin to set your territory
            from <strong>Admin → Regional Sales</strong>.
          </Alert>
        )}

        {/* Tabs. Each tab registers what it's showing; the Excel button on
            the right downloads the active tab (Team Chat has none). */}
        <SectionExportProvider about={exportAboutFor(mode)}>
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ mb: 2.5, borderBottom: '1px solid rgba(187, 140, 63, 0.22)' }}
          >
            <Tabs
              value={mode}
              onChange={(_, v: Mode) => setMode(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                flex: 1,
                minWidth: 0,
                '& .MuiTab-root': {
                  fontFamily: FONT_INTER,
                  fontSize: { xs: 13, md: 13.5 },
                  fontWeight: 600,
                  textTransform: 'none',
                  letterSpacing: 0,
                  minHeight: 44,
                  px: { xs: 1.5, md: 2 },
                },
                '& .Mui-selected': { color: `${GOLD} !important` },
                '& .MuiTabs-indicator': { bgcolor: GOLD, height: 2 },
              }}
            >
              <Tab value="today" label="Today’s Plan" />
              <Tab value="tracker" label="My Territories" />
              <Tab value="added" label="Manually Added Accounts" />
              <Tab value="noship" label="No Shipments Yet" />
              <Tab value="logs" label="My Logs" />
              <Tab
                value="notes"
                label={
                  <Stack direction="row" alignItems="center" gap={0.75}>
                    <span>Team Chat</span>
                    {unreadNotes > 0 && (
                      <Box
                        sx={{
                          minWidth: 18,
                          height: 18,
                          px: 0.5,
                          borderRadius: '9px',
                          bgcolor: GOLD,
                          color: '#fff',
                          fontSize: 11,
                          fontWeight: 700,
                          lineHeight: '18px',
                          textAlign: 'center',
                        }}
                      >
                        {unreadNotes}
                      </Box>
                    )}
                  </Stack>
                }
              />
            </Tabs>
            <SectionExportButton accent={GOLD} />
          </Stack>

          {/* Render the FollowUpTracker for the active mode.  Mounting
            a fresh component on tab switch resets its internal state
            (active bucket, filters) — that's the desired UX: each tab
            starts clean. */}
          {mode === 'today' && <TodaysPlanTab />}
          {mode === 'tracker' && (
            // My Territories mirrors the Depletions Follow-Up Tracker
            // shape, plus one action: Log Visit. Reps searching their
            // territory out in market can log on the spot instead of
            // hunting for the account in Today's Plan. Pin / skip / flag
            // stay plan-only.
            <FollowUpTracker
              useDataSource={useTerritoryData}
              rowActions={(acc) =>
                isLoggedToday(acc.last_visit_date) ? (
                  <LoggedTodayCell
                    account={acc}
                    onAddNote={() =>
                      setLogVisitTarget({ accountId: acc.account_id, accountName: acc.name })
                    }
                  />
                ) : (
                  <Stack spacing={0.75} sx={{ width: { xs: 128, sm: 160 } }}>
                    <LogVisitButton
                      onClick={() =>
                        setLogVisitTarget({ accountId: acc.account_id, accountName: acc.name })
                      }
                      tooltip={
                        <FormattedTooltipBody
                          eyebrow="Log visit"
                          title="Log what happened on this visit or call."
                          body="Saved to the account's timeline and your My Logs."
                        />
                      }
                    />
                    <LastLoggedLine account={acc} />
                  </Stack>
                )
              }
              rowActionsHeader="Log a visit"
              rowActionsDescription="Log a visit or call without leaving your territory list. Once logged, the row shows today's outcome."
              defaultBucket="30-60 days"
              stateOptions={allStateOptions}
              territoryStates={territoryStates}
              onStateFilterChange={setTerritoryStateFilter}
            />
          )}
          {mode === 'added' && (
            <AddedAccountsTab
              onAddAccount={() => setAddAccountOpen(true)}
              onLogVisit={setLogVisitTarget}
            />
          )}
          {mode === 'noship' && <NoShipmentsTab onLogVisit={setLogVisitTarget} />}
          {mode === 'logs' && <MyLogsTab onAddAccount={() => setAddAccountOpen(true)} />}
          {mode === 'notes' && <NotesInbox />}
        </SectionExportProvider>
      </Container>

      {logVisitTarget && (
        <VisitNoteDialog
          open
          accountId={logVisitTarget.accountId}
          accountName={logVisitTarget.accountName}
          onClose={() => setLogVisitTarget(null)}
          onSuccess={() => {
            setLogVisitTarget(null);
            void queryClient.invalidateQueries({ queryKey: ['field'] });
          }}
        />
      )}

      <AddAccountDialog
        open={addAccountOpen}
        onClose={() => setAddAccountOpen(false)}
        // Pre-fill with the rep's first territory state — the
        // overwhelmingly common case, and they can change it.
        defaultStateCode={territoryStates[0] ?? null}
        onCreated={(accountId) => {
          setAddAccountOpen(false);
          // The new account changes today's plan and the territory
          // list, so drop the cached field queries before we go.
          void queryClient.invalidateQueries({ queryKey: ['field'] });
          navigate(`/regional-sales/accounts/${accountId}`);
        }}
      />
    </SiteLayout>
  );
}

/**
 * The primary "Log Visit" call to action — shared by Today's Plan and
 * My Territories so a rep can log from wherever they found the account.
 *
 * Light premium treatment: ivory-cream background, warm-gold lettering
 * and a refined gold edge — reads as an "engraved jewelry-box label"
 * rather than a flat SaaS gold rectangle or a dark whiskey foil. Hover
 * warms the cream toward champagne and deepens the type. Full width of
 * its container, so callers size it.
 */
function LogVisitButton({ onClick, tooltip }: { onClick: () => void; tooltip: ReactNode }) {
  return (
    <Tooltip title={tooltip} arrow slotProps={BRAND_TOOLTIP_SLOT_PROPS}>
      <Button
        size="small"
        variant="contained"
        disableElevation
        fullWidth
        startIcon={<EditNoteIcon sx={{ fontSize: 15, mr: 0.5 }} />}
        onClick={onClick}
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          // White-to-ivory gradient suggests a bevelled edge
          // catching light — a polished cream surface.
          background: 'linear-gradient(180deg, #ffffff 0%, #fdf9f0 100%)',
          color: GOLD,
          px: 1.25,
          py: 0.85,
          minHeight: 36,
          borderRadius: 0.75,
          border: '1px solid rgba(187, 140, 63, 0.55)',
          // Cream inner top highlight + cool soft drop shadow.
          boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 1px 2px rgba(15, 10, 7, 0.05)',
          transition:
            'background 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
          '& .MuiButton-startIcon': {
            color: GOLD,
            transition: 'color 0.2s ease, transform 0.2s ease',
          },
          '&:hover': {
            // Warm champagne tint — cream backed by GOLD_LIGHT.
            background: 'linear-gradient(180deg, #fdf9f0 0%, rgba(233, 196, 106, 0.22) 100%)',
            color: '#8e6a2a',
            borderColor: 'rgba(187, 140, 63, 0.85)',
            boxShadow:
              'inset 0 1px 0 rgba(255, 255, 255, 0.85), 0 6px 16px rgba(187, 140, 63, 0.22)',
            transform: 'translateY(-1px)',
            '& .MuiButton-startIcon': {
              color: '#8e6a2a',
              transform: 'translateY(-0.5px)',
            },
          },
          '&:active': {
            transform: 'translateY(0)',
            background: 'rgba(233, 196, 106, 0.28)',
            boxShadow: 'inset 0 1px 2px rgba(15, 10, 7, 0.10)',
          },
          '&:focus-visible': {
            outline: `2px solid ${GOLD}`,
            outlineOffset: 2,
          },
        }}
      >
        Log Visit
      </Button>
    </Tooltip>
  );
}

/** 'Today 10:42 AM' · 'Yesterday 4:05 PM' · 'Oct 3, 2:15 PM' · 'Oct 3 2025, 2:15 PM'. */
function loggedWhenLabel(iso: string): string | null {
  const d = new Date(iso); // full timestamp with zone — safe to parse directly
  if (isNaN(d.getTime())) return null;
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86_400_000);
  if (dayDiff === 0) return `Today ${time}`;
  if (dayDiff === 1) return `Yesterday ${time}`;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const day = d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
  return `${day}, ${time}`;
}

/** 'Prasad Yalavarthy' → 'Prasad Y.' — fits the narrow actions column. */
function shortName(full: string): string {
  const [first = '', ...rest] = full.trim().split(/\s+/);
  const last = rest.at(-1);
  return last ? `${first} ${last[0]}.` : first;
}

/** Everything the "last logged" UI shows, derived once per row. */
function lastLoggedParts(account: FollowUpAccount) {
  const visitDate = account.last_visit_date;
  if (!visitDate) return null;
  const loggedAt = account.last_logged_at ? new Date(account.last_logged_at) : null;
  const validLoggedAt = loggedAt && !isNaN(loggedAt.getTime()) ? loggedAt : null;
  const outcomeKey = account.last_outcome ?? null;
  const visitLabel = parseBackendDate(visitDate).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const loggedFull = validLoggedAt?.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  const outcome = outcomeKey ? (OUTCOME_LABEL[outcomeKey] ?? outcomeKey) : null;
  return {
    // Older API responses carry only the visit date.
    when: account.last_logged_at
      ? loggedWhenLabel(account.last_logged_at)
      : parseBackendDate(visitDate).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
        }),
    time: validLoggedAt?.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }),
    by: account.last_logged_by ? shortName(account.last_logged_by) : null,
    outcome,
    tone: (outcomeKey && OUTCOME_TONE[outcomeKey]) || '#7a7a78',
    tooltip: [
      `Visit date: ${visitLabel}`,
      loggedFull &&
        `Logged: ${loggedFull}${account.last_logged_by ? ` by ${account.last_logged_by}` : ''}`,
      outcome && `Outcome: ${outcome}`,
    ]
      .filter(Boolean)
      .join('\n'),
  };
}

const EYEBROW_SX = {
  fontFamily: FONT_INTER,
  fontSize: 9.5,
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  lineHeight: 1.5,
} as const;

/** One fixed line of the "last logged" UI — never wraps mid-phrase. */
function InfoLine({ children, muted = false }: { children: ReactNode; muted?: boolean }) {
  return (
    <Typography
      noWrap
      sx={{
        fontFamily: FONT_INTER,
        fontSize: muted ? 10.5 : 11,
        lineHeight: 1.45,
        color: muted ? 'rgba(15, 10, 7, 0.5)' : 'rgba(15, 10, 7, 0.7)',
      }}
    >
      {children}
    </Typography>
  );
}

/** Small coloured dot marking an outcome. */
function OutcomeDot({ tone }: { tone: string }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        width: 6,
        height: 6,
        borderRadius: '50%',
        bgcolor: tone,
        mr: 0.6,
        verticalAlign: '1px',
      }}
    />
  );
}

/**
 * Quiet "last touch" caption under a row's Log Visit button, on fixed
 * lines so nothing splits mid-phrase in the 128px phone column:
 * "● FOLLOW-UP" (letter-spaced, in the outcome's colour) / "🕘 Yesterday,
 * 4:05 PM" / "by Jane S.". Hover for the full record.
 */
function LastLoggedLine({ account }: { account: FollowUpAccount }) {
  const parts = lastLoggedParts(account);
  if (!parts) return null;
  return (
    <Tooltip
      title={<Box sx={{ whiteSpace: 'pre-line' }}>{parts.tooltip}</Box>}
      arrow
      slotProps={BRAND_TOOLTIP_SLOT_PROPS}
    >
      <Box sx={{ px: 0.25, cursor: 'default', minWidth: 0 }}>
        {parts.outcome && (
          <Typography noWrap sx={{ ...EYEBROW_SX, color: parts.tone }}>
            <OutcomeDot tone={parts.tone} />
            {parts.outcome}
          </Typography>
        )}
        <InfoLine muted>
          <AccessTimeRoundedIcon
            sx={{ fontSize: 11, mr: 0.4, verticalAlign: '-1px', color: 'rgba(15, 10, 7, 0.4)' }}
          />
          {parts.when}
        </InfoLine>
        {parts.by && <InfoLine muted>by {parts.by}</InfoLine>}
      </Box>
    </Tooltip>
  );
}

/**
 * Row state once a visit is logged today — the counterpart of the Log
 * Visit button: same width, radius and cream surface, so the column
 * keeps its rhythm. A green "LOGGED TODAY" header, the outcome as a
 * coloured dot, and an "Add note" action segment along the bottom (the
 * same tray idea as Pin / Skip / Flag on Today's Plan).
 */
function LoggedTodayCell({
  account,
  onAddNote,
}: {
  account: FollowUpAccount;
  onAddNote: () => void;
}) {
  const parts = lastLoggedParts(account);
  const green = '#22865a';
  return (
    <Box
      sx={{
        width: { xs: 140, sm: 160 },
        borderRadius: 0.75,
        border: `1px solid ${green}59`,
        background: 'linear-gradient(180deg, #ffffff 0%, #f5faf7 100%)',
        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 1px 2px rgba(15, 10, 7, 0.05)',
        overflow: 'hidden',
      }}
    >
      <Tooltip
        title={<Box sx={{ whiteSpace: 'pre-line' }}>{parts?.tooltip ?? 'Logged today'}</Box>}
        arrow
        slotProps={BRAND_TOOLTIP_SLOT_PROPS}
      >
        <Box sx={{ px: 1.1, pt: 0.85, pb: 0.75, cursor: 'default' }}>
          <Stack direction="row" alignItems="center" gap={0.6}>
            <CheckCircleIcon sx={{ fontSize: 14, color: green }} />
            <Typography sx={{ ...EYEBROW_SX, color: green }}>Logged today</Typography>
          </Stack>
          {parts && (
            <Box sx={{ mt: 0.35, minWidth: 0 }}>
              <InfoLine>
                {parts.outcome && (
                  <>
                    <OutcomeDot tone={parts.tone} />
                    <Box component="span" sx={{ fontWeight: 600, color: INK }}>
                      {parts.outcome}
                    </Box>
                  </>
                )}
                {parts.outcome && parts.time && ' · '}
                {parts.time}
              </InfoLine>
              {parts.by && <InfoLine muted>by {parts.by}</InfoLine>}
            </Box>
          )}
        </Box>
      </Tooltip>
      <Tooltip
        title="Edit or add another note for today"
        arrow
        slotProps={BRAND_TOOLTIP_SLOT_PROPS}
      >
        <ButtonBase
          onClick={onAddNote}
          sx={{
            width: '100%',
            gap: 0.6,
            py: 0.7,
            borderTop: '1px solid rgba(187, 140, 63, 0.22)',
            bgcolor: 'rgba(253, 249, 240, 0.8)',
            color: GOLD,
            fontFamily: FONT_INTER,
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            transition: 'background-color 0.2s ease, color 0.2s ease',
            '&:hover': { bgcolor: 'rgba(233, 196, 106, 0.2)', color: '#8e6a2a' },
            '&.Mui-focusVisible': { outline: `2px solid ${GOLD}`, outlineOffset: -2 },
          }}
        >
          <EditNoteIcon sx={{ fontSize: 14 }} />
          Add note
        </ButtonBase>
      </Tooltip>
    </Box>
  );
}

/**
 * Today's Plan tab — same FollowUpTracker as the Tracker tab, but
 * with three extras per row:
 *
 *   1. A "Why" reason chip — explains why the system surfaced this
 *      account (pinned, declining, sweet spot, etc.) so the rep can
 *      trust or override the pick.
 *   2. A "Skip for today" button — dismisses a row from the day's
 *      list without committing anything to the database.  Session-
 *      local; a refresh restores the dismissed rows.
 *   3. A small "N skipped — show" hint at the top when the rep has
 *      dismissed anything, so they can recover from a misclick.
 */
function TodaysPlanTab() {
  const queryClient = useQueryClient();
  // Rep profile for the "Near you" badge.  Same query key as the
  // parent FieldPage and the data hook, so this is a free read off
  // the TanStack cache — no extra round-trip.
  const profileQuery = useQuery({
    queryKey: ['field', 'me'],
    queryFn: fetchMyRepProfile,
    staleTime: 60_000,
  });
  const rep = profileQuery.data;

  // Same list as My Territories: the rep's own states first, everywhere
  // else under "Other states".
  const statesQuery = useFieldStates();
  const todayStateOptions = useMemo(() => {
    const all = statesQuery.data?.states ?? [];
    return all.length > 0 ? all : (rep?.territory_states?.slice().sort() ?? []);
  }, [statesQuery.data, rep?.territory_states]);
  const [logVisitFor, setLogVisitFor] = useState<{
    accountId: number;
    accountName: string;
  } | null>(null);
  const [flagFor, setFlagFor] = useState<{
    accountId: number;
    accountName: string;
  } | null>(null);
  const [skippedIds, setSkippedIds] = useState<Set<number>>(new Set());
  // Lifted from FollowUpTracker so useFilteredTodayData can switch to
  // the full territory dataset when the rep filters by a specific state.
  const [todayStateFilter, setTodayStateFilter] = useState<string[]>([]);
  // Success Snackbar — fired by VisitNoteDialog's onSuccess so the
  // rep gets an explicit "Logged · {name}" moment of confirmation
  // on top of the passive row fade.  Holds enough data to render
  // the outcome chip in the toast.
  const [logToast, setLogToast] = useState<{
    accountName: string;
    outcome: string;
  } | null>(null);

  const pinMutation = useMutation({
    mutationFn: ({ accountId, next }: { accountId: number; next: boolean }) =>
      next ? pinAccount(accountId) : unpinAccount(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field', 'follow-up'] });
      queryClient.invalidateQueries({ queryKey: ['field', 'today-plan'] });
    },
  });

  // Single data source for Today's Plan.  Handles both cases in one
  // place to avoid two competing layers fighting over what to show:
  //   • No state filter → top-N curated picks, local-first (same
  //     scoring logic as useTodaysPlanData in field.ts).
  //   • State filter active → ALL follow-up accounts in those states,
  //     no TODAY_PLAN_LIMIT, so the rep sees every account that needs
  //     attention regardless of proximity.
  const useFilteredTodayData = () => {
    // No selection -> no states param -> the rep's territory, exactly as
    // before. The curated picks are deliberately unchanged; the filter is
    // the only way to reach another state from here.
    const full = useFieldFollowUp(todayStateFilter);
    const profileQ = useQuery({
      queryKey: ['field', 'me'],
      queryFn: fetchMyRepProfile,
      staleTime: 60_000,
    });
    return useMemo(() => {
      if (!full.data) return full;
      const home = profileQ.data
        ? { home_zip: profileQ.data.home_zip, home_state: profileQ.data.home_state }
        : null;
      const all = full.data.buckets.flatMap((b) =>
        b.accounts.map((a) => ({
          account: a,
          bucketLabel: b.label,
          score: todayScore(a, b.label, home),
          isLocal: home ? nearHome(a, home) !== null : false,
        })),
      );
      let accounts: FollowUpAccount[];
      if (todayStateFilter.length > 0) {
        const filtered = all
          .filter((x) => todayStateFilter.includes(x.account.state_code ?? ''))
          .filter((x) => !skippedIds.has(x.account.account_id))
          .filter((x) => x.score >= 0);
        filtered.sort((a, b) => b.score - a.score);
        accounts = filtered.map((x) => x.account);
      } else {
        const MIN_LOCAL = 5;
        const localPool = all.filter((x) => x.isLocal && x.score >= 0);
        const useLocal = home?.home_zip != null && localPool.length >= MIN_LOCAL;
        const pool = useLocal ? localPool : all.filter((x) => x.score >= 0);
        pool.sort((a, b) => b.score - a.score);
        const picked = pool
          .slice(0, TODAY_PLAN_LIMIT)
          .map((x) => x.account)
          .filter((a) => !skippedIds.has(a.account_id));
        picked.sort((a, b2) => {
          const aDone = isLoggedToday(a.last_visit_date) ? 1 : 0;
          const bDone = isLoggedToday(b2.last_visit_date) ? 1 : 0;
          return aDone - bDone;
        });
        accounts = picked;
      }
      const synth: FollowUpTrackerResponse = {
        ...full.data,
        buckets: [
          {
            label: TODAY_PLAN_BUCKET_LABEL,
            count: accounts.length,
            total_9l: accounts.reduce((s, a) => s + (Number(a.total_9l) || 0), 0).toString(),
            accounts,
          },
        ],
      };
      return { ...full, data: synth };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [full, todayStateFilter, skippedIds, profileQ.data]);
  };

  // Logged-today progress — drives the encouragement strip + the
  // per-row fade.  Reads straight off the picks (after skip filter,
  // before logged-sort) so the counts match what's on screen.
  const todayInner = useTodaysPlanData();
  const visiblePicks = useMemo(() => {
    const accounts = todayInner.data?.buckets?.[0]?.accounts ?? [];
    return accounts.filter((a) => !skippedIds.has(a.account_id));
  }, [todayInner.data, skippedIds]);
  const loggedCount = visiblePicks.filter((a) => isLoggedToday(a.last_visit_date)).length;
  const totalCount = visiblePicks.length;

  // Excel export. The on-screen order is the tracker's — its own search
  // box and sort sit on top of useFilteredTodayData — so the tracker hands
  // us its rows (already skip-filtered, every page) and we shape them into
  // a printable visit list.
  const buildTodaysPlanExport = (
    accounts: FollowUpAccount[],
    trackerFilters: ExportFilter[],
  ): ExcelWorkbookSpec => {
    const home = rep ? { home_zip: rep.home_zip, home_state: rep.home_state } : null;
    const rows = accounts.map((a, i) => {
      const prox = nearHome(a, home);
      return {
        ...a,
        order: i + 1,
        reason: getTodayReason({
          is_pinned: !!a.is_pinned,
          velocity_pct: a.velocity_pct,
          days_since: a.days_since,
          products: a.products,
        }),
        coreMissing: missingCoreWhiskey(a.products),
        proximity: prox === 'zip5' ? 'In your ZIP' : prox === 'zip3' ? 'Near you' : '',
      };
    });
    return {
      title: 'Today’s Plan',
      filters: [
        {
          label: 'Plan scope',
          value:
            todayStateFilter.length > 0
              ? `Every follow-up account in ${[...todayStateFilter].sort().join(', ')}`
              : `Top ${TODAY_PLAN_LIMIT} picks for your territory`,
        },
        ...trackerFilters,
        { kind: 'info', label: 'Skipped accounts excluded', value: String(skippedIds.size) },
      ],
      sheets: [
        excelSheet({
          name: 'Today’s Plan',
          rows,
          columns: [
            { header: '#', value: (r) => r.order, format: 'int', width: 5 },
            ...accountColumns<(typeof rows)[number]>(),
            { header: 'Why now', value: (r) => r.reason.label, width: 22 },
            { header: 'Why (detail)', value: (r) => r.reason.detail, width: 44 },
            { header: 'Pinned', value: (r) => (r.is_pinned ? 'Yes' : ''), width: 8 },
            {
              header: 'Logged today',
              value: (r) => (isLoggedToday(r.last_visit_date) ? 'Yes' : ''),
              width: 9,
            },
            { header: 'Proximity', value: (r) => r.proximity, width: 12 },
            {
              header: 'Last visit',
              value: (r) => isoDate(r.last_visit_date),
              format: 'date',
            },
            {
              header: 'Last outcome',
              value: (r) =>
                r.last_outcome ? (OUTCOME_LABEL[r.last_outcome] ?? r.last_outcome) : '',
              width: 13,
            },
            {
              header: 'Missing core',
              value: (r) => (r.coreMissing.length ? `No ${r.coreMissing.join(' or ')}` : ''),
              width: 20,
            },
            { header: 'Distributor', value: (r) => r.distributor_name, width: 26 },
            {
              header: 'Premises',
              value: (r) => premisesLabel(r.premises_type, r.is_online_retailer),
              width: 12,
            },
            { header: 'Last active', value: (r) => isoDate(r.last_active), format: 'date' },
            { header: 'Days since', value: (r) => r.days_since, format: 'int' },
            { header: 'Total 9L', value: (r) => num(r.total_9l), format: 'decimal' },
            { header: 'Recent 3M 9L', value: (r) => num(r.recent_3m_9l), format: 'decimal' },
            { header: 'Prior 3M 9L', value: (r) => num(r.prior_3m_9l), format: 'decimal' },
            {
              header: 'Velocity %',
              value: (r) => (r.velocity_pct === null ? null : r.velocity_pct / 100),
              format: 'percent',
            },
            { header: 'Products carried', value: (r) => r.products.join(', '), width: 30 },
            {
              header: 'Missing products',
              value: (r) => (r.missing_products ?? []).join(', '),
              width: 30,
            },
          ],
        }),
      ],
    };
  };

  // "Why now" — replaces the FollowUpTracker's auto-generated
  // ACTION cell (tier badge + "No automatic recommendation matched"
  // jargon) with a plain-English narrative the rep can scan in one
  // breath:
  //   1. Signal chip in the matching tone ("Sweet spot for re-order")
  //   2. One-sentence explanation (the reason.detail prose)
  //   3. Compact context strip (proximity + last-touch chips)
  // The tier classifier (REVIEW / RECOVERY) is a useful manager
  // taxonomy but it doesn't help a rep decide what to do.  We hide
  // it on Today's Plan and surface it where it belongs — Browse
  // Buckets, where the rep is exploring rather than executing.
  const renderActionCellForToday = (account: FollowUpAccount) => {
    const isPinned = !!account.is_pinned;
    const reason = getTodayReason({
      is_pinned: isPinned,
      velocity_pct: account.velocity_pct,
      days_since: account.days_since,
      products: account.products,
    });
    const coreMissing = missingCoreWhiskey(account.products);
    const home = rep ? { home_zip: rep.home_zip, home_state: rep.home_state } : null;
    const prox = nearHome(account, home);
    const proxLabel = prox === 'zip5' ? 'In your ZIP' : prox === 'zip3' ? 'Near you' : null;
    const lastVisit = account.last_visit_date ?? null;
    const lastOutcome = account.last_outcome ?? null;
    const lastChip = lastVisit ? daysAgoLabel(lastVisit) : null;
    // When the account is pinned, the primary reason chip reads "Pinned
    // by you" and hides the core-whiskey gap — surface it in the context
    // strip so the flag never disappears. For non-pinned gaps the primary
    // chip already IS the gap, so we don't repeat it here.
    const showCoreGapChip = isPinned && coreMissing.length > 0;
    const hasContextStrip = !!proxLabel || !!(lastChip && lastOutcome) || showCoreGapChip;
    return (
      <Stack direction="column" spacing={0.75} sx={{ maxWidth: { xs: 200, sm: 230 } }}>
        <Chip
          label={reason.label}
          size="small"
          sx={{
            alignSelf: 'flex-start',
            height: 22,
            fontSize: 10.5,
            fontFamily: FONT_INTER,
            fontWeight: 700,
            letterSpacing: '0.04em',
            bgcolor: `${reason.tone}1a`,
            color: reason.tone,
            border: `1px solid ${reason.tone}55`,
          }}
        />
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 12,
            color: 'rgba(15, 10, 7, 0.7)',
            lineHeight: 1.45,
          }}
        >
          {reason.detail}
        </Typography>
        {hasContextStrip && (
          <Box
            sx={{
              pt: 0.5,
              borderTop: '1px dashed rgba(187, 140, 63, 0.28)',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.4,
              rowGap: 0.4,
            }}
          >
            {showCoreGapChip && (
              <Chip
                label={`No ${coreMissing.join(' or ')}`}
                size="small"
                sx={{
                  height: 18,
                  fontSize: 10,
                  fontFamily: FONT_INTER,
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  bgcolor: '#9a34121a',
                  color: '#9a3412',
                  border: '1px solid #9a341255',
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
            {proxLabel && (
              <Chip
                label={proxLabel}
                size="small"
                sx={{
                  height: 18,
                  fontSize: 10,
                  fontFamily: FONT_INTER,
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  bgcolor: 'rgba(187, 140, 63, 0.08)',
                  color: GOLD,
                  border: '1px solid rgba(187, 140, 63, 0.30)',
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
            {lastChip && lastOutcome && (
              <Chip
                label={`Last: ${lastChip}`}
                size="small"
                sx={{
                  height: 18,
                  fontSize: 10,
                  fontFamily: FONT_INTER,
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  bgcolor: 'rgba(15, 10, 7, 0.04)',
                  color: 'rgba(15, 10, 7, 0.55)',
                  border: '1px solid rgba(15, 10, 7, 0.14)',
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
          </Box>
        )}
      </Stack>
    );
  };

  const renderRowActions = (account: FollowUpAccount) => {
    const isPinned = !!account.is_pinned;
    const lastVisit = account.last_visit_date ?? null;

    // Once the rep has logged a visit today, the row swaps from
    // pin / log / skip buttons into the "Logged today" card. Keeps the
    // row visible (in case the rep needs to edit) but signals "done" so
    // attention moves to undone picks; the card's "Add note" strip lets
    // reps correct mistakes.
    const doneToday = isLoggedToday(lastVisit);
    if (doneToday) {
      return (
        <LoggedTodayCell
          account={account}
          onAddNote={() =>
            setLogVisitFor({ accountId: account.account_id, accountName: account.name })
          }
        />
      );
    }

    // Two clearly-stacked sections so the eye reads top-down:
    //   1. PRIMARY  — the one mandatory next step (Log Visit) — sized
    //                 to its content so it doesn't look stretched and
    //                 cheap; subtle gradient + tinted border give it a
    //                 tactile, premium feel without shouting.
    //   2. SECONDARY — pin / skip / flag as icon+label units, tightly
    //                  grouped so they read as one set of "other
    //                  options" beneath the CTA.
    return (
      <Stack
        direction="column"
        spacing={1.1}
        sx={{ minWidth: { xs: 140, sm: 160 }, maxWidth: { xs: 170, sm: 180 } }}
      >
        {/* 1 · PRIMARY CTA — full width so it matches the Pin/Skip/Flag
            tray below; the two read as one cohesive panel. */}
        <LogVisitButton
          onClick={() =>
            setLogVisitFor({ accountId: account.account_id, accountName: account.name })
          }
          tooltip={
            <FormattedTooltipBody
              eyebrow="Mandatory"
              title="Log what happened on this visit or call."
              body="Required for every account before you move on to the next."
            />
          }
        />

        {/* 2 · SECONDARY actions — segmented "tray" so Pin / Skip
            / Flag read as one related set of "other options".  Hairline
            gold dividers between cells echo the editorial palette used
            elsewhere (territory panel, 6-pk | 9L stat group). */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            borderRadius: 1.25,
            border: '1px solid rgba(187, 140, 63, 0.28)',
            bgcolor: 'rgba(255, 255, 255, 0.45)',
            overflow: 'hidden',
            '& > * + *': {
              borderLeft: '1px solid rgba(187, 140, 63, 0.22)',
            },
          }}
        >
          <SecondaryAction
            icon={
              isPinned ? (
                <PushPinIcon sx={{ fontSize: 16 }} />
              ) : (
                <PushPinOutlinedIcon sx={{ fontSize: 16 }} />
              )
            }
            label={isPinned ? 'Pinned' : 'Pin'}
            tooltip={
              isPinned ? (
                <FormattedTooltipBody
                  eyebrow="Pinned"
                  title="Locked to the top of tomorrow’s picks."
                  body="Click to unpin and let ranking signals decide."
                />
              ) : (
                <FormattedTooltipBody
                  eyebrow="Pin"
                  title="Lock to the top of tomorrow’s picks."
                  body="Overrides decline, proximity, and other ranking signals."
                />
              )
            }
            onClick={() => pinMutation.mutate({ accountId: account.account_id, next: !isPinned })}
            tone={isPinned ? GOLD : 'rgba(15, 10, 7, 0.55)'}
            activeBg={isPinned ? 'rgba(187, 140, 63, 0.14)' : 'transparent'}
            hoverBg="rgba(187, 140, 63, 0.10)"
          />
          <SecondaryAction
            icon={<CloseIcon sx={{ fontSize: 16 }} />}
            label="Skip"
            tooltip={
              <FormattedTooltipBody
                eyebrow="Skip for today"
                title="Hide this row from today’s view only."
                body="Recorded in My Logs, but nothing changes — refresh or click Show all at the top to bring it back."
              />
            }
            onClick={() => {
              const next = new Set(skippedIds);
              next.add(account.account_id);
              setSkippedIds(next);
              // Fire-and-forget: the skip still only affects THIS
              // session's view, but it leaves a trace in My Logs so
              // "I skipped that, I was there Tuesday" survives. A
              // failure here must never block dismissing the row.
              void recordSkip(account.account_id).catch(() => undefined);
            }}
            tone="rgba(15, 10, 7, 0.55)"
            activeBg="transparent"
            hoverBg="rgba(15, 10, 7, 0.05)"
          />
          <SecondaryAction
            icon={<OutlinedFlagIcon sx={{ fontSize: 16 }} />}
            label="Flag"
            tooltip={
              <FormattedTooltipBody
                eyebrow="Flag for admin"
                title="Escalate an account-level issue."
                body="Closed store, ownership change, wrong address. Not a visit outcome — no cooldown."
              />
            }
            onClick={() => setFlagFor({ accountId: account.account_id, accountName: account.name })}
            tone="#a86b1e"
            activeBg="transparent"
            hoverBg="rgba(168, 107, 30, 0.08)"
          />
        </Box>
        <LastLoggedLine account={account} />
      </Stack>
    );
  };

  // "Tracking from your home" banner — surfaces what the system is
  // using to pick local accounts.  Two purposes:
  //   1. Self-diagnosis — if the address shown is wrong / missing,
  //      the rep knows to fix it in Account Settings rather than
  //      wonder why their picks are off.
  //   2. Honesty — declares that Today's Plan is geographically
  //      scoped so picks outside the metro don't look like bugs.
  const homeZip3 = rep?.home_zip?.replace(/\D/g, '').slice(0, 3) ?? null;
  const homeCity = rep?.home_city ?? null;
  const homeStateCode = rep?.home_state ?? null;
  return (
    <>
      {skippedIds.size > 0 && (
        <Box
          sx={{
            mb: 1.5,
            p: 1,
            borderRadius: 1,
            bgcolor: 'rgba(187, 140, 63, 0.06)',
            border: '1px solid rgba(187, 140, 63, 0.18)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            justifyContent: 'space-between',
            flexWrap: 'wrap',
          }}
        >
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 12.5,
              color: 'rgba(15, 10, 7, 0.7)',
            }}
          >
            <strong>{skippedIds.size}</strong> account{skippedIds.size === 1 ? '' : 's'} skipped
            from today’s plan.
          </Typography>
          <Button
            size="small"
            startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={() => setSkippedIds(new Set())}
            sx={{
              fontFamily: FONT_INTER,
              fontWeight: 600,
              fontSize: 12,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: GOLD,
              '&:hover': { bgcolor: 'rgba(187, 140, 63, 0.08)' },
            }}
          >
            Show all
          </Button>
        </Box>
      )}

      <DailyProgressStrip
        logged={loggedCount}
        total={totalCount}
        homeCity={homeCity}
        homeStateCode={homeStateCode}
        homeZip3={homeZip3}
      />

      {/* How today's picks are ranked — reflects todayScore (pinned →
          urgency → momentum → proximity → lifetime), not the standard
          dashboard buckets. Right-aligned above the picks table. */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <TodaysPlanRankingInfo />
      </Box>

      <FollowUpTracker
        buildExport={buildTodaysPlanExport}
        useDataSource={useFilteredTodayData}
        rowActions={renderRowActions}
        rowActionsHeader="Your next move"
        defaultBucket={TODAY_PLAN_BUCKET_LABEL}
        hideHeading
        hideFilterChips
        getRowSx={(acc) => (isLoggedToday(acc.last_visit_date) ? { opacity: 0.55 } : {})}
        actionColumnLabel="Why now"
        actionCellOverride={(acc) => renderActionCellForToday(acc)}
        explainerRows={TODAY_PLAN_EXPLAINER_ROWS}
        stateOptions={todayStateOptions}
        territoryStates={rep?.territory_states ?? []}
        onStateFilterChange={setTodayStateFilter}
      />

      {logVisitFor && (
        <VisitNoteDialog
          open
          accountId={logVisitFor.accountId}
          accountName={logVisitFor.accountName}
          onClose={() => setLogVisitFor(null)}
          onSuccess={({ outcome, accountName }) =>
            setLogToast({ accountName, outcome: OUTCOME_LABEL[outcome] ?? outcome })
          }
        />
      )}

      {flagFor && (
        <FlagAccountDialog
          open
          accountId={flagFor.accountId}
          accountName={flagFor.accountName}
          onClose={() => setFlagFor(null)}
        />
      )}

      {/* Success Snackbar — pops on the bottom-right with a brand
          cream-and-gold treatment so it reads as part of the editorial
          palette, not an MUI default toast.  Auto-dismisses after
          2.6s, slides in/out from the right. */}
      <Snackbar
        open={!!logToast}
        autoHideDuration={2600}
        onClose={() => setLogToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slots={{ transition: Slide }}
        slotProps={{ transition: { direction: 'left' } as never }}
      >
        <Box
          role="status"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 1.75,
            py: 1.25,
            minWidth: 280,
            maxWidth: 380,
            borderRadius: 1.25,
            bgcolor: IVORY,
            border: '1px solid rgba(187, 140, 63, 0.42)',
            boxShadow: '0 12px 28px rgba(15, 10, 7, 0.14), 0 2px 6px rgba(15, 10, 7, 0.06)',
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              bgcolor: 'rgba(34, 134, 90, 0.14)',
              border: '1px solid rgba(34, 134, 90, 0.4)',
              color: '#22865a',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: '#22865a',
                lineHeight: 1,
              }}
            >
              Logged · {logToast?.outcome}
            </Typography>
            <Typography
              sx={{
                mt: 0.4,
                fontFamily: FONT_INTER,
                fontSize: 12.5,
                fontWeight: 600,
                color: INK,
                lineHeight: 1.35,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {logToast?.accountName}
            </Typography>
            <Typography
              sx={{
                mt: 0.2,
                fontFamily: FONT_INTER,
                fontSize: 11.5,
                color: 'rgba(15, 10, 7, 0.55)',
                lineHeight: 1.4,
              }}
            >
              Moving on — the next pick is ready.
            </Typography>
          </Box>
        </Box>
      </Snackbar>
    </>
  );
}

/**
 * Page-level Territory panel — sits above the tabs so both Today's
 * Plan and Tracker render against the same explicit "here's what you
 * own" context.
 */
function TerritoryPanel({ territoryStates }: { territoryStates: string[] }) {
  const { data } = useFieldFollowUp();
  const territory = data?.territory ?? null;
  const window = data?.data_window ?? null;
  const windowLabel = formatDataWindow(window);

  return (
    <Box
      sx={{
        position: 'relative',
        p: { xs: 2, md: 2.25 },
        borderRadius: 1.5,
        bgcolor: 'rgba(187, 140, 63, 0.05)',
        border: '1px solid rgba(187, 140, 63, 0.22)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: GOLD,
            }}
          >
            Your Territory
          </Typography>
          <TerritoryStates states={territoryStates} />
        </Stack>
        <Stack direction="row" spacing={1.25} alignItems="center">
          {windowLabel && (
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 11.5,
                color: 'rgba(15, 10, 7, 0.55)',
                display: { xs: 'none', sm: 'inline' },
              }}
            >
              <Box
                component="span"
                sx={{
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: '0.20em',
                  textTransform: 'uppercase',
                  color: 'rgba(15, 10, 7, 0.45)',
                  mr: 0.6,
                }}
              >
                Source
              </Box>
              <Box component="span" sx={{ fontWeight: 700, color: 'rgba(15, 10, 7, 0.75)' }}>
                VIP iDIG
              </Box>
              <Box component="span" sx={{ color: 'rgba(15, 10, 7, 0.35)', mx: 0.6 }}>
                ·
              </Box>
              {windowLabel}
            </Typography>
          )}
          {territory && (
            <TerritoryHelpTooltip
              total={territory.total_accounts}
              withShipments={territory.with_shipment_history}
              without={territory.without_shipment_history}
              windowLabel={windowLabel}
            />
          )}
        </Stack>
      </Box>

      {territory && (
        <Box
          sx={{
            mt: 1.25,
            pt: 1.25,
            borderTop: '1px dashed rgba(187, 140, 63, 0.32)',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            columnGap: { xs: 2, md: 3 },
            rowGap: 1.25,
          }}
        >
          {/* Stats packed at the start */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              flexWrap: 'wrap',
              columnGap: { xs: 2, md: 3 },
              rowGap: 0.75,
            }}
          >
            <TerritoryStat value={territory.total_accounts} label="In territory" tone={GOLD} />
            <Divider />
            <TerritoryStat
              value={territory.with_shipment_history}
              label="With shipment history"
              tone="#22865a"
            />
            <Divider />
            <TerritoryStat
              value={territory.without_shipment_history}
              label="No shipments yet"
              tone="#7a7a78"
            />
          </Box>

          {/* Coverage bar — fills the empty right side with a real
              signal: how much of the territory the depletions data
              actually covers.  Stacks below on mobile. */}
          <TerritoryCoverageBar
            withShipments={territory.with_shipment_history}
            total={territory.total_accounts}
          />
        </Box>
      )}
    </Box>
  );
}

function TerritoryCoverageBar({ withShipments, total }: { withShipments: number; total: number }) {
  const pct = total > 0 ? Math.round((withShipments / total) * 100) : 0;
  return (
    <Box
      sx={{
        ml: { md: 'auto' },
        minWidth: { xs: '100%', md: 260 },
        maxWidth: { md: 340 },
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          mb: 0.6,
          gap: 1,
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'rgba(15, 10, 7, 0.5)',
          }}
        >
          Coverage
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 12.5,
            fontWeight: 700,
            color: '#22865a',
            letterSpacing: '-0.005em',
          }}
        >
          {pct}%{' '}
          <Box component="span" sx={{ fontWeight: 500, color: 'rgba(15, 10, 7, 0.55)' }}>
            have shipment data
          </Box>
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          height: 7,
          borderRadius: 4,
          overflow: 'hidden',
          bgcolor: 'rgba(122, 122, 120, 0.20)',
          border: '1px solid rgba(15, 10, 7, 0.06)',
        }}
      >
        <Box
          sx={{
            width: `${pct}%`,
            bgcolor: '#22865a',
            transition: 'width 0.4s ease',
          }}
        />
      </Box>
    </Box>
  );
}

function Divider() {
  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'block' },
        width: '1px',
        alignSelf: 'stretch',
        bgcolor: 'rgba(187, 140, 63, 0.22)',
      }}
    />
  );
}

function TerritoryStat({ value, label, tone }: { value: number; label: string; tone: string }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 0.75,
      }}
    >
      <Typography
        component="span"
        sx={{
          fontFamily: FONT_INTER,
          fontWeight: 700,
          fontSize: { xs: 20, md: 22 },
          lineHeight: 1,
          letterSpacing: '-0.015em',
          color: tone,
        }}
      >
        {value.toLocaleString()}
      </Typography>
      <Typography
        component="span"
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 12.5,
          fontWeight: 500,
          color: 'rgba(15, 10, 7, 0.68)',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function TerritoryHelpTooltip({
  total,
  withShipments,
  without,
  windowLabel,
}: {
  total: number;
  withShipments: number;
  without: number;
  windowLabel: string | null;
}) {
  return (
    <Tooltip
      arrow
      placement="left"
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: '#fdf9f0',
            color: INK,
            border: '1px solid rgba(187, 140, 63, 0.32)',
            boxShadow: '0 6px 22px rgba(15, 10, 7, 0.12)',
            fontFamily: FONT_INTER,
            fontSize: 12.5,
            lineHeight: 1.55,
            maxWidth: 420,
            p: 1.75,
            '& strong': { color: GOLD, fontWeight: 700 },
          },
        },
        arrow: {
          sx: {
            color: '#fdf9f0',
            '&::before': { border: '1px solid rgba(187, 140, 63, 0.32)' },
          },
        },
      }}
      title={
        <Box>
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: GOLD,
              mb: 1,
            }}
          >
            How these counts are built
          </Typography>
          <Box component="span" sx={{ display: 'block', mb: 0.85 }}>
            <strong>{total.toLocaleString()}</strong> — every account in the depletions master list
            whose state matches your territory.
          </Box>
          <Box component="span" sx={{ display: 'block', mb: 0.85 }}>
            <strong>{withShipments.toLocaleString()}</strong> — the subset that has at least one
            non-zero shipment recorded in the depletions feed. These get bucketed below by days
            since their last shipment.
          </Box>
          <Box component="span" sx={{ display: 'block', mb: windowLabel ? 0.85 : 0 }}>
            <strong>{without.toLocaleString()}</strong> — accounts in your territory the depletions
            feed has never recorded a shipment for. Could be newly-added retail locations,
            prospects, or accounts that do not move HY product. They sit outside the buckets because
            there is no "last shipment" to bucket by.
          </Box>
          {windowLabel && (
            <Box
              component="span"
              sx={{
                display: 'block',
                mt: 1,
                pt: 1,
                borderTop: '1px dashed rgba(187, 140, 63, 0.32)',
                fontSize: 11.5,
                color: 'rgba(15, 10, 7, 0.65)',
                lineHeight: 1.55,
              }}
            >
              <Box
                component="span"
                sx={{
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: GOLD,
                }}
              >
                Source · VIP iDIG
              </Box>
              <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                Depletions data is sourced from <strong>VIP iDIG</strong>, the standard three-tier
                industry feed from Vermont Information Processing (vtinfo.com) that connects
                suppliers, distributors, and retailers.
              </Box>
              <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                Records cover <strong>{windowLabel}</strong> — the buckets and counts above are
                computed across every shipment in this range.
              </Box>
            </Box>
          )}
        </Box>
      }
    >
      <Box
        component="span"
        sx={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          bgcolor: 'rgba(187, 140, 63, 0.18)',
          color: GOLD,
          fontSize: 12,
          fontWeight: 700,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'help',
          border: '1px solid rgba(187, 140, 63, 0.36)',
        }}
      >
        ?
      </Box>
    </Tooltip>
  );
}

// =====================================================================
// Today's Plan — daily progress strip
// =====================================================================
//
// Soft-enforcement nudge: shows N of M logged today with adaptive
// copy that escalates from "Start your day" → "Halfway there" →
// "All clear, great hustle".  Replaces the strip entirely with a
// celebration card when every visible pick is logged.
//
// Design intent (per the workflow-discipline discussion): visibility
// not blocking.  The rep can still move freely between rows; this
// just makes the un-done work obvious without faking accountability.
// Manager-side accountability rides on the existing admin Activity
// dashboard.
function DailyProgressStrip({
  logged,
  total,
  homeCity,
  homeStateCode,
  homeZip3,
}: {
  logged: number;
  total: number;
  homeCity: string | null;
  homeStateCode: string | null;
  homeZip3: string | null;
}) {
  if (total === 0) return null;

  const pct = Math.round((logged / total) * 100);
  const allDone = logged === total;

  // Compose a single, weave-in location label that reads naturally
  // inside the body sentence ("20 picks ready in Jacksonville, FL").
  // Falls back to a generic phrasing when home_zip isn't set so the
  // strip stays informative without showing "(null)" or similar.
  const locationLabel = homeZip3
    ? homeCity
      ? `${homeCity}${homeStateCode ? `, ${homeStateCode}` : ''}`
      : `your home metro (${homeZip3}xx)`
    : null;

  // Tier-based encouragement.  Each tier has a short headline + body
  // — sparingly worded so it doesn't feel like a coach barking at
  // the rep.  Body adapts to the actual count so the message lands
  // honestly ("4 to wrap up", not generic "almost there!"), and
  // weaves the location into the early-day framing when it's set.
  let headline: string;
  let body: string;
  let strokeColor: string;
  if (allDone) {
    headline = 'All clear — every touchpoint logged.';
    body = `Great hustle today. ${total} accounts logged.`;
    strokeColor = '#22865a';
  } else if (pct === 0) {
    headline = 'Start your day with the first account.';
    body = locationLabel
      ? `${total} picks ready in ${locationLabel} (${homeZip3}xx). Log a quick note after each one to keep momentum.`
      : `${total} picks ready. Log a quick note after each one to keep momentum.`;
    strokeColor = GOLD;
  } else if (pct < 50) {
    headline = `You're underway — ${logged} down, ${total - logged} to go.`;
    body = 'Keep moving. The unlogged accounts are sorted to the top.';
    strokeColor = GOLD;
  } else if (pct < 100) {
    headline = `Halfway there — ${total - logged} left.`;
    body = `${logged} of ${total} logged. Push through the rest.`;
    strokeColor = GOLD;
  } else {
    // Should not reach — allDone short-circuits above.
    headline = '';
    body = '';
    strokeColor = GOLD;
  }

  // Secondary line — only on the first tier (pct === 0) and only when
  // it's actually useful.  Two variants:
  //   * home_zip set → "Falls back to your full territory…" caveat
  //   * home_zip empty → small CTA to set the home address
  // Suppressed on later tiers so the strip stays terse once work is
  // underway.
  const showFootnote = pct === 0 && !allDone;
  const footnote: ReactNode | null = showFootnote ? (
    homeZip3 ? (
      'Falls back to your full territory if there aren’t enough nearby picks.'
    ) : (
      <>
        Set a home ZIP in{' '}
        <Box
          component={Link}
          to="/account-settings"
          sx={{ color: GOLD, fontWeight: 600, textDecoration: 'none' }}
        >
          Account Settings
        </Box>{' '}
        to focus on picks near you.
      </>
    )
  ) : null;

  return (
    <Box
      sx={{
        mb: 1.5,
        p: 1.5,
        borderRadius: 1.25,
        bgcolor: allDone ? 'rgba(34, 134, 90, 0.08)' : 'rgba(187, 140, 63, 0.06)',
        border: `1px solid ${allDone ? 'rgba(34, 134, 90, 0.32)' : 'rgba(187, 140, 63, 0.28)'}`,
        // Two-column scorecard layout:
        //   left  — visual progress (counter / bar / percent)
        //   right — editorial guidance (headline / body / footnote)
        // Stacks vertically on xs so phones don't get crushed.
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '230px 1fr' },
        columnGap: { sm: 2.25 },
        rowGap: { xs: 1.5, sm: 0 },
        alignItems: 'stretch',
      }}
    >
      {/* LEFT — scorecard. Counter pill + progress bar + percent
          stacked vertically.  Bordered on the right at sm+ so the
          two halves read as a paired widget rather than two stripes
          of equal weight. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          pr: { sm: 2.25 },
          borderRight: {
            sm: `1px solid ${allDone ? 'rgba(34, 134, 90, 0.28)' : 'rgba(187, 140, 63, 0.32)'}`,
          },
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: allDone ? 'rgba(34, 134, 90, 0.16)' : 'rgba(187, 140, 63, 0.16)',
            border: `1px solid ${allDone ? 'rgba(34, 134, 90, 0.34)' : 'rgba(187, 140, 63, 0.36)'}`,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: strokeColor,
          }}
        >
          {allDone ? (
            <EmojiEventsIcon sx={{ fontSize: 28 }} />
          ) : (
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontWeight: 800,
                fontSize: 16,
                lineHeight: 1,
                letterSpacing: '-0.02em',
              }}
            >
              {logged}/{total}
            </Typography>
          )}
        </Box>
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              flexGrow: 1,
              height: 5,
              borderRadius: 2,
              bgcolor: 'rgba(15, 10, 7, 0.08)',
              '& .MuiLinearProgress-bar': {
                bgcolor: strokeColor,
                transition: 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
              },
            }}
          />
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.12em',
              color: strokeColor,
              fontFeatureSettings: '"tnum"',
            }}
          >
            {pct}%
          </Typography>
        </Box>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'rgba(15, 10, 7, 0.5)',
          }}
        >
          Logged Today
        </Typography>
      </Box>

      {/* RIGHT — guidance.  Headline, body, optional footnote.
          Vertically centered to balance the scorecard's tall block.
          On xs the strip stacks (grid → 1fr), so the gold rule that
          normally lives on the left column's right edge moves to
          this column's top edge — keeps the two halves visually
          paired even on phones. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minWidth: 0,
          pt: { xs: 1.5, sm: 0 },
          borderTop: {
            xs: `1px dashed ${allDone ? 'rgba(34, 134, 90, 0.28)' : 'rgba(187, 140, 63, 0.32)'}`,
            sm: 'none',
          },
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 14,
            fontWeight: 700,
            color: INK,
            lineHeight: 1.35,
            mb: 0.4,
          }}
        >
          {headline}
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 12.5,
            color: 'rgba(15, 10, 7, 0.66)',
            lineHeight: 1.5,
          }}
        >
          {body}
        </Typography>
        {footnote && (
          <Typography
            sx={{
              mt: 0.6,
              fontFamily: FONT_INTER,
              fontSize: 11.5,
              color: 'rgba(15, 10, 7, 0.5)',
              lineHeight: 1.45,
            }}
          >
            {footnote}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// =====================================================================
// Secondary row action — icon + text label stacked vertically so the
// rep doesn't have to hover for a tooltip to know what each button
// does.  Used for Pin / Skip / Flag on Today's Plan rows.
// =====================================================================
// Segmented cell inside the secondary-actions tray.  Each cell fills
// its grid column, holds the icon + caption stacked vertically, and
// runs a soft hover state that hints "this is interactive" without
// the heavy bordered look the per-button outline used to carry.
function SecondaryAction({
  icon,
  label,
  tooltip,
  onClick,
  tone,
  activeBg,
  hoverBg,
}: {
  icon: ReactNode;
  label: string;
  /** Tooltip content — accepts ReactNode so callers can pass a
   *  structured FormattedTooltipBody (eyebrow + title + body) for
   *  editorial polish, not just a plain string. */
  tooltip: ReactNode;
  onClick: () => void;
  tone: string;
  /** Background tint applied when the action is in an "on" state
   *  (e.g. Pin when the row is already pinned).  Transparent when no
   *  active state applies. */
  activeBg: string;
  hoverBg: string;
}) {
  return (
    <Tooltip title={tooltip} arrow slotProps={BRAND_TOOLTIP_SLOT_PROPS}>
      <Box
        component="button"
        onClick={onClick}
        sx={{
          appearance: 'none',
          border: 'none',
          background: activeBg,
          padding: 0,
          py: 0.85,
          margin: 0,
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.45,
          color: tone,
          transition: 'background-color 0.18s ease, transform 0.18s ease',
          '&:hover': {
            bgcolor: hoverBg,
            '& .sa-icon': { transform: 'translateY(-1px)' },
          },
          '&:active .sa-icon': { transform: 'translateY(0)' },
          '&:focus-visible': {
            outline: `2px solid ${tone}`,
            outlineOffset: -2,
          },
        }}
      >
        <Box
          className="sa-icon"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: tone,
            transition: 'transform 0.18s ease',
            lineHeight: 0,
          }}
        >
          {icon}
        </Box>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: tone,
            lineHeight: 1,
          }}
        >
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
}
